package com.trevorwiebe.timesheet.admin.rate.data

import com.trevorwiebe.timesheet.admin.rate.domain.RateRepository
import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.core.domain.dto.RateDto
import com.trevorwiebe.timesheet.core.domain.model.Rate
import com.trevorwiebe.timesheet.core.domain.model.toRate
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.CollectionReference
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable

/**
 * Document shape written for a rate. Mirrors the legacy admin panel exactly — the
 * Firestore document id is the rate's identity, so [RateDto.id] is not persisted.
 */
@Serializable
private data class RateWriteDto(
    val description: String,
    val userFacing: Boolean,
    val defaultRate: Boolean,
    val rateCode: String,
    val overtimeRateCode: String
)

/** Partial-update payload used to flip the default flag without touching other fields. */
@Serializable
private data class RateDefaultDto(
    val defaultRate: Boolean
)

class RateRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository
) : RateRepository {

    override suspend fun getRates(): Flow<TSResult> {
        val orgIdResult = coreRepository.getOrganizationId()
        if (orgIdResult.error != null) return flow { emit(orgIdResult) }
        val orgId = orgIdResult.data as String

        val flow = firebaseDatabase.firestore
            .collection("organizations")
            .document(orgId)
            .collection("rates")
            .snapshots
            .map { snapshot ->
                snapshot.documents
                    .map { doc -> doc.data<RateDto>().copy(id = doc.id).toRate() }
                    .filter { it.userFacing }
            }
            .map { TSResult(data = it) }

        return FirestoreListenerRegistry.registerFlow(flow)
    }

    override suspend fun addRate(rate: Rate): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            val ratesCollection = ratesCollection(orgId)
            if (rate.defaultRate) clearOtherDefaults(ratesCollection, exceptId = null)
            ratesCollection.add(
                RateWriteDto(
                    description = rate.description,
                    userFacing = true,
                    defaultRate = rate.defaultRate,
                    rateCode = rate.rateCode,
                    overtimeRateCode = rate.overtimeRateCode
                )
            )
            TSResult(data = "Rate added successfully")
        }
    }

    override suspend fun updateRate(rate: Rate): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            val ratesCollection = ratesCollection(orgId)
            if (rate.defaultRate) clearOtherDefaults(ratesCollection, exceptId = rate.id)
            ratesCollection
                .document(rate.id)
                .set(
                    RateWriteDto(
                        description = rate.description,
                        userFacing = rate.userFacing,
                        defaultRate = rate.defaultRate,
                        rateCode = rate.rateCode,
                        overtimeRateCode = rate.overtimeRateCode
                    ),
                    merge = true
                )
            TSResult(data = "Rate updated successfully")
        }
    }

    private fun ratesCollection(orgId: String): CollectionReference =
        firebaseDatabase.firestore
            .collection("organizations")
            .document(orgId)
            .collection("rates")

    /**
     * Clears the default flag on every other rate so exactly one default exists at a
     * time. [exceptId] is the rate being saved (null when adding, since it has no id yet).
     */
    private suspend fun clearOtherDefaults(ratesCollection: CollectionReference, exceptId: String?) {
        val documents = ratesCollection.get().documents
        for (document in documents) {
            if (document.id == exceptId) continue
            val isDefault = document.data<RateDto>().copy(id = document.id).defaultRate
            if (isDefault) {
                ratesCollection
                    .document(document.id)
                    .set(RateDefaultDto(defaultRate = false), merge = true)
            }
        }
    }
}
